import { Base } from "../../framework/base";
import { IIdTitleNode } from "./idTitleNode";

export interface ITreeNode {
    id: string;
    name: string;
    comment: string;
    children: ITreeNode[];
    parent: ITreeNode;
    level: number;
    collapsed: boolean;
    disabled: boolean;

    getIsRoot(): boolean;
    getHasChildren(): boolean;
    getParentIsRoot(): boolean;
    getAncesterIsCollapsed(): boolean;
    getSiblings(): ITreeNode[];
    getSiblingNames(lowerCase: boolean): string[];
    getTreeNodeById(id: string): ITreeNode;
    setCollapsed(value: boolean);
    getAsList(list: ITreeNode[], level: number): ITreeNode[];
}

export class TreeNode implements ITreeNode {
    id: string;
    name: string;
    comment: string;
    children: TreeNode[];
    parent: TreeNode;
    level: number;
    collapsed: boolean;
    disabled: boolean;

    constructor();
    constructor(obj: ITreeNode);
    constructor(obj?: any) {
        this.id = obj && obj.id || "";
        this.name = obj && obj.name || "";
        this.comment = obj && obj.comment || "";
        this.level = obj && obj.level || 0;
        this.collapsed = obj && obj.collapsed || false;
        this.disabled = obj && obj.disabled || false;
        this.children = [];
        this.parent = null;
        if (obj && obj.children) {
            this.children = Base.getTypedArray(obj.children, TreeNode);
            for (const child of this.children) {
                child.parent = this;
            }
        }
    }

    getIsRoot(): boolean {
        return !this.parent;
    }

    getHasChildren(): boolean {
        return this.children.length > 0;
    }

    getParentIsRoot(): boolean {
        if (!this.parent) return false;
        return this.parent.getIsRoot();
    }

    getAncesterIsCollapsed(): boolean {
        if (!this.parent) return false;
        if (this.parent.collapsed) return true;
        return this.parent.getAncesterIsCollapsed();
    }

    getSiblings(): ITreeNode[] {
        if (this.getIsRoot()) return [];
        return this.parent.children.filter(i => i.id !== this.id);
    }

    getTreeNodeById(id: string): ITreeNode {
        if (!id) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (const child of this.children) {
            const childResult = child.getTreeNodeById(id);
            if (childResult) return childResult;
        }
        return null;
    }

    getSiblingNames(lowerCase: boolean): string[] {
        if (!this.parent) return [];
        return this.parent.children.filter(i => i.id !== this.id).map(i => lowerCase ? i.name.toLowerCase() : i.name);
    }

    setCollapsed(value: boolean) {
        this.collapsed = value;
    }

    getAsList(list: ITreeNode[], level: number = 0): ITreeNode[] {
        list.push(this);
        this.level = level;
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getAsList(list, level + 1);
        }
        return list;
    }

    static getTreeNodeById(items: ITreeNode[], id: string): ITreeNode {
        if (!items || items.length < 1) return null;
        for (const item of items) {
            const result = item.getTreeNodeById(id);
            if (!result) continue;
            return result;
        }
        return null;
    }
    
    static getTreeNodes(items: IIdTitleNode[], parent: ITreeNode = null, level: number = 0): ITreeNode[] {
        if (!items || items.length < 1) return [];
        const result: ITreeNode[] = [];
        for (const item of items) {
            const treeNode = new TreeNode();
            treeNode.id = item.id;
            treeNode.name = item.title;
            treeNode.comment = "";
            treeNode.children = TreeNode.getTreeNodes(item.children, treeNode, level + 1);
            treeNode.parent = parent;
            treeNode.level = level;
            treeNode.collapsed = false;
            treeNode.disabled = !item.active;
            result.push(treeNode);
        }
        return result;
    }
}